/**
 * wd-recent-products.js — localStorage store for products opened from search.
 *
 * Replaces the earlier wd-recent-searches.js, which stored the raw text the
 * customer typed. That was wrong: typing "ir" and opening Lipo Iron left a chip
 * reading "ir" in the panel. We now store the *product that was opened*.
 *
 * Storage only: no rendering, no DOM, no listeners. <wd-predictive-search> owns
 * presentation and calls in here. Exposed as window.WdRecentProducts, matching
 * the existing window.WdCartPackCount global idiom (Classic Wonder, D-2).
 *
 * Record shape (deliberately minimal — never the full product object):
 *   { v: 1, url, title, image, price, compare, available, ts }
 *
 * Rules:
 *   - de-duplicated by canonical product path; a repeat opens moves to the top
 *   - newest first, capped (default 4)
 *   - localStorage only: no cookies, no backend, no Shopify storage, no account
 *   - every access guarded — search must never break because history is broken
 *
 * Known limitation: price/availability are a snapshot from when the product was
 * opened and can go stale. Accepted: re-fetching on every panel open would cost
 * a network request for a decorative strip. The product page is always correct.
 */
(function () {
  'use strict';

  if (window.WdRecentProducts) return;

  var KEY = 'wd:recent-products';
  var LEGACY_KEY = 'wd:recent-searches'; /* raw query strings — removed on load */
  var PENDING_KEY = 'wd:pending-search-product';
  var PENDING_TTL_MS = 10 * 60 * 1000; /* 10 minutes */
  var VERSION = 2;
  var DEFAULT_MAX = 4;
  var HARD_MAX = 12;

  function clampMax(max) {
    var n = parseInt(max, 10);
    if (isNaN(n) || n < 1) n = DEFAULT_MAX;
    return Math.min(n, HARD_MAX);
  }

  /* Compare on path only: /products/lipo-iron?variant=1 and /he-il/products/lipo-iron
     are the same product for this purpose. */
  function canonical(url) {
    var s = String(url || '').split('#')[0].split('?')[0];
    var m = s.match(/\/products\/[^/]+/);
    return (m ? m[0] : s).replace(/\/+$/, '').toLowerCase();
  }

  /**
   * Accept only well-formed records. This is also the migration path: legacy
   * entries were plain strings, so they fail `typeof === object` and are
   * dropped rather than rendered as broken rows.
   */
  function valid(rec) {
    return (
      rec &&
      typeof rec === 'object' &&
      !Array.isArray(rec) &&
      typeof rec.url === 'string' &&
      rec.url.length > 0 &&
      typeof rec.title === 'string' &&
      rec.title.length > 0
    );
  }

  function handleOf(url) {
    var m = canonical(url).match(/\/products\/([^/]+)$/);
    return m ? m[1] : '';
  }

  function normalise(rec) {
    return {
      handle: typeof rec.handle === 'string' && rec.handle ? rec.handle : handleOf(rec.url),
      url: String(rec.url),
      title: String(rec.title),
      image: typeof rec.image === 'string' && rec.image ? rec.image : '',
      price: typeof rec.price === 'string' ? rec.price : '',
      /* v1 called this `compare`; accept both so an in-flight upgrade keeps data. */
      compareAtPrice:
        typeof rec.compareAtPrice === 'string'
          ? rec.compareAtPrice
          : typeof rec.compare === 'string'
          ? rec.compare
          : '',
      available: rec.available !== false,
      timestamp:
        typeof rec.timestamp === 'number' && isFinite(rec.timestamp)
          ? rec.timestamp
          : typeof rec.ts === 'number' && isFinite(rec.ts)
          ? rec.ts
          : 0,
    };
  }

  /**
   * Reads both the v2 envelope `{version, items}` and the bare v1 array, so an
   * upgrade keeps existing history instead of silently wiping it.
   */
  function read() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) return [];
      var parsed = JSON.parse(raw);
      var items = Array.isArray(parsed)
        ? parsed
        : parsed && typeof parsed === 'object' && Array.isArray(parsed.items)
        ? parsed.items
        : [];
      return items.filter(valid).map(normalise);
    } catch (e) {
      return [];
    }
  }

  function write(list) {
    try {
      window.localStorage.setItem(KEY, JSON.stringify({ version: VERSION, items: list }));
      return true;
    } catch (e) {
      return false;
    }
  }

  /* One-time cleanup of the old text-query history. Runs on load; failure is
     irrelevant because nothing reads that key any more. */
  function dropLegacy() {
    try {
      if (window.localStorage.getItem(LEGACY_KEY) !== null) {
        window.localStorage.removeItem(LEGACY_KEY);
      }
    } catch (e) {
      /* storage unavailable — nothing to clean up */
    }
  }
  dropLegacy();

  window.WdRecentProducts = {
    KEY: KEY,
    LEGACY_KEY: LEGACY_KEY,
    VERSION: VERSION,

    get: function (max) {
      return read().slice(0, clampMax(max));
    },

    /**
     * Record a product the customer actually opened.
     * Returns the new list; ignores anything without a url and title.
     */
    add: function (product, max) {
      if (!valid(product)) return read();

      var rec = normalise(product);
      if (!rec.timestamp) rec.timestamp = new Date().getTime();

      var key = canonical(rec.url);
      var list = read().filter(function (r) {
        return canonical(r.url) !== key;
      });

      list.unshift(rec);
      list = list.slice(0, clampMax(max));
      write(list);
      return list;
    },

    clear: function () {
      try {
        window.localStorage.removeItem(KEY);
      } catch (e) {
        /* already effectively empty for the customer */
      }
      return [];
    },

    /* ---- pending marker: survives parameter stripping and a missing referrer ----
       sessionStorage, so it dies with the tab and never leaks between sessions. */

    setPending: function (url) {
      if (!url) return false;
      try {
        window.sessionStorage.setItem(
          PENDING_KEY,
          JSON.stringify({ url: canonical(url), t: new Date().getTime() })
        );
        return true;
      } catch (e) {
        return false;
      }
    },

    /** True when a non-expired marker points at this product. */
    matchPending: function (url) {
      try {
        var raw = window.sessionStorage.getItem(PENDING_KEY);
        if (!raw) return false;
        var m = JSON.parse(raw);
        if (!m || typeof m.url !== 'string' || typeof m.t !== 'number') return false;
        if (new Date().getTime() - m.t > PENDING_TTL_MS) return false;
        return m.url === canonical(url);
      } catch (e) {
        return false;
      }
    },

    clearPending: function () {
      try {
        window.sessionStorage.removeItem(PENDING_KEY);
      } catch (e) {
        /* nothing to do */
      }
    },
  };
})();
